import type { ChangeEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import type { Boat } from "../../../../common/src";
import { boatPhotoUrl } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { prepareImage } from "../../utils/prepareImage";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { ConfirmButton } from "../shared/ConfirmButton";
import { Notice } from "../shared/Notice";
import { EmptyState } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  preview: {
    display: "block",
    width: "100%",
    maxWidth: 520,
    aspectRatio: "3 / 2",
    objectFit: "cover",
    borderRadius: theme.radius,
    border: `1px solid ${theme.color.border}`,
    background: theme.color.background,
  },
  fileInput: { display: "none" },
}));

interface BoatPhotoCardProps {
  boat: Boat;
  onChanged: (boat: Boat) => void;
}

export const BoatPhotoCard = ({ boat, onChanged }: BoatPhotoCardProps) => {
  const classes = useStyles();
  const form = useFormStyles();
  const fileInput = useRef<HTMLInputElement>(null);
  const [preparing, setPreparing] = useState(false);

  const upload = useAction(
    useCallback(
      async (file: File) => {
        const image = await prepareImage(file);
        return trpc.boat.setPhoto.mutate({ boatId: boat.id, ...image });
      },
      [boat.id]
    )
  );
  const remove = useAction(
    useCallback(
      () => trpc.boat.removePhoto.mutate({ boatId: boat.id }),
      [boat.id]
    )
  );

  const onFileChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still triggers a change.
    event.target.value = "";
    if (!file) {
      return;
    }
    setPreparing(true);
    const updated = await upload.run(file);
    setPreparing(false);
    if (updated) {
      onChanged(updated);
    }
  };

  const onRemove = async () => {
    const updated = await remove.run();
    if (updated) {
      onChanged(updated);
    }
  };

  const busy = preparing || upload.pending || remove.pending;
  const error = upload.error ?? remove.error;

  return (
    <Card title="Photo">
      {boat.photoUpdatedAt ? (
        <img
          className={classes.preview}
          src={boatPhotoUrl(boat.id, boat.photoUpdatedAt)}
          alt={`Photo of ${boat.name}`}
        />
      ) : (
        <EmptyState title="No photo yet">
          Add a photo so veterans can see the boat they're reserving.
        </EmptyState>
      )}
      {error && (
        <Notice
          kind="error"
          onDismiss={() => {
            upload.clearError();
            remove.clearError();
          }}
        >
          {error}
        </Notice>
      )}
      <input
        ref={fileInput}
        className={classes.fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        onChange={(e) => void onFileChosen(e)}
      />
      <div className={form.actions}>
        <Button disabled={busy} onClick={() => fileInput.current?.click()}>
          {busy && !remove.pending
            ? "Uploading…"
            : boat.photoUpdatedAt
              ? "Replace photo"
              : "Upload photo"}
        </Button>
        {boat.photoUpdatedAt && (
          <ConfirmButton
            label="Remove photo"
            confirmLabel="Yes, remove photo"
            pending={remove.pending}
            onConfirm={() => void onRemove()}
          />
        )}
      </div>
    </Card>
  );
};
